// Presentation + decision helpers for the Reconciliation Cockpit.
// Business logic (save/process payloads, ordering of link candidates) lives in
// index.js and the reused PvRecordReconciliation modal — this file only maps
// statuses/actions to labels, colours and recommendations.

// ---- action catalogue ----
export const ACT = {
  RECONCILED: { label: 'Reconcile', key: '1', kind: 'primary', verb: 'reconciled' },
  ATA_ARN:    { label: 'Transfer (ATA/ARN)', key: '2', kind: '', verb: 'queued a transfer for' },
  SOLD:       { label: 'Mark sold', key: '3', kind: '', verb: 'marked sold' },
  UNSOLD:     { label: 'Unsold', key: '3', kind: '', verb: 'un-sold' },
  NEW_ASSET:  { label: 'Create asset', key: '4', kind: '', verb: 'created an asset from' },
  LINK_ASSET: { label: 'Link to asset', key: '1', kind: 'primary', verb: 'linked' },
  IGNORE:     { label: 'Ignore', key: '5', kind: 'danger', verb: 'ignored' },
  // Assets tab only: Process raises an Asset Condition Voucher (condition Missing).
  MISSING:    { label: 'Mark missing', key: '6', kind: '', verb: 'marked missing' }
}

// AST: SOLD swaps to UNSOLD per-row for already-sold assets (see astActionsFor).
export const AST_ACTIONS = ['ATA_ARN', 'SOLD', 'IGNORE']
export const PV_ACTIONS = ['LINK_ASSET', 'NEW_ASSET', 'IGNORE']

// ---- reconcile status metadata (AST). PV records use the synthetic PV_NEW. ----
export const STATUS_META = {
  FULLY_MATCHED:          { label: 'Fully matched', cls: 'matched', flagged: false, why: 'Code, location and sub-location all agree with the register.' },
  MISMATCHED_SUBLOCATION: { label: 'Sub Location Mismatched', cls: 'subloc', flagged: true, why: 'Right location, but a different sub-location than the register.' },
  MISMATCHED_LOCATION:    { label: 'Location Mismatched', cls: 'loc', flagged: true, why: 'Found in a different location than the register shows.' },
  NOT_FOUND:              { label: 'Location + Sub Mismatched', cls: 'notfound', flagged: true, why: 'Found far from the register — both location and sub-location differ.' },
  INVALID_RECORD:         { label: 'Not in register', cls: 'invalid', flagged: true, why: 'This asset code does not exist in the master register.' },
  DUPLICATE_RECORD:       { label: 'Duplicate scan', cls: 'dup', flagged: true, why: 'This code was scanned more than once in this audit.' },
  SOLD:                   { label: 'Marked Sold', cls: 'sold', flagged: false, why: 'The register marks this asset as already sold.' },
  PV_NEW:                 { label: 'New item (PV)', cls: 'new', flagged: true, why: 'A physical item with no master record — link it to an asset or create one.' }
}

// ---- display grouping ----
// Business rule: a location and its sub-location are a PAIR. Once the location
// is wrong the record is wrong, whether or not the sub-location happens to line
// up. So MISMATCHED_LOCATION (location differs, sub-location same) and NOT_FOUND
// (both differ) are ONE bucket in the UI.
//
// This is a presentation grouping only. The record's own reconcileStatus is
// never rewritten, the payloads sent to save/process are untouched, and the
// scanned-vs-register table in the focus panel still shows both fields, so the
// underlying distinction is preserved and auditable.
export const STATUS_GROUP = {
  MISMATCHED_LOCATION: 'LOCATION_OFF',
  NOT_FOUND: 'LOCATION_OFF'
}

export const groupOf = (status) => STATUS_GROUP[status] || status

export const GROUP_META = {
  LOCATION_OFF: {
    label: 'Location Mismatched', cls: 'loc', flagged: true,
    why: 'Found in a different location than the register shows. Location and sub-location are treated as a pair, so the sub-location is not judged separately once the location is wrong.'
  }
}

// Metadata for a grouped key: group first, then the raw per-status table.
export const metaForGroup = (key) => GROUP_META[key] || STATUS_META[key] || null

// Display order for the AST "current status" breakdown, best -> worst, in GROUPED
// keys. Any key the backend produces that is NOT in this list is still shown
// (appended, with its raw code as the label) rather than dropped, so an unknown
// status can never be silently hidden from the count. PV_NEW is excluded: PV
// records carry no reconcileStatus at all.
export const AST_STATUS_ORDER = [
  'FULLY_MATCHED', 'MISMATCHED_SUBLOCATION', 'LOCATION_OFF',
  'INVALID_RECORD', 'DUPLICATE_RECORD', 'SOLD'
]

// Sentinel bucket for AST records that come back with no reconcileStatus. Shown
// explicitly instead of being folded into another status - missing is not zero.
export const NO_STATUS = '__NO_STATUS__'

export const statusMeta = (record) => {
  if (record.source === 'PV') return STATUS_META.PV_NEW
  if (record.source === 'ASSET') {
    if (record.recStatus && ACT[record.recStatus]) return { label: ACT[record.recStatus].label, cls: 'done', flagged: false, why: 'Reconciled from the audit.' }
    return { label: 'In register', cls: 'sold', flagged: false, why: 'This asset exists in the master register.' }
  }
  // Grouped first (LOCATION_OFF covers MISMATCHED_LOCATION + NOT_FOUND), then the
  // raw status, then an honest fallback showing the unknown code as-is.
  return metaForGroup(groupOf(record.reconcileStatus))
    || { label: record.reconcileStatus || '—', cls: 'sold', flagged: false, why: '' }
}

// Recommended action for the focus panel's "suggested" highlight.
export const recommended = (record) => {
  if (record.source === 'PV') return 'LINK_ASSET'
  switch (record.reconcileStatus) {
    case 'FULLY_MATCHED': return 'IGNORE'
    case 'MISMATCHED_LOCATION':
    case 'MISMATCHED_SUBLOCATION':
    case 'NOT_FOUND': return 'ATA_ARN'
    case 'INVALID_RECORD': return 'NEW_ASSET'
    case 'DUPLICATE_RECORD': return 'IGNORE'
    // The register says this asset was sold, yet the auditor physically found it,
    // so the sale is the thing that looks wrong - suggest reversing it. (SOLD is
    // not even offered on these rows; astActionsFor gives them UNSOLD/IGNORE.)
    case 'SOLD': return 'UNSOLD'
    default: return null
  }
}

// ---- id → name resolvers ----
export const locName = (id, locations) => {
  if (!id || !locations || !locations.length) return ''
  const i = locations.findIndex(l => l._id === id)
  return i >= 0 ? locations[i].name : ''
}
export const clientName = (id, clients) => {
  if (!id || !clients || !clients.length) return ''
  const i = clients.findIndex(c => c._id === id)
  return i >= 0 ? clients[i].name : ''
}

// Stable, guaranteed-unique per-record key. We prefer the _uid stamped at load
// time because the raw _id is NOT reliably unique — a PV assetDetail can inherit
// its parent PV's _id, so `source:_id` collides and React duplicates rows.
export const rkey = (r) => r._uid || `${r.source}:${r._id}`

// Owner list out of the shared filter payload.
export const ownerOptions = (filters) =>
  (filters && filters['actionTaken.newAssetOwner'] && filters['actionTaken.newAssetOwner']['data'] &&
    filters['actionTaken.newAssetOwner']['data']['data']) || []
